import * as Y from 'yjs';
import { Awareness, applyAwarenessUpdate, encodeAwarenessUpdate } from 'y-protocols/awareness';
import type { CollaboratorAwareness } from './awareness';
import { assignColor } from './awareness';
import { useCommentStore } from '@/store/commentStore';
import type { Comment } from '@/api/comments';

// ── Base64 helpers ───────────────────────────────────────────

function uint8ToBase64(data: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]!);
  }
  return btoa(binary);
}

function base64ToUint8(str: string): Uint8Array {
  const binary = atob(str);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ── Types ────────────────────────────────────────────────────

interface WebSocketProviderConfig {
  wsEndpoint: string;
  mapId: string;
  authToken: string;
  user: { userId: string; name: string };
}

interface WsMessage {
  action: string;
  data?: string;
}

// ── Provider ─────────────────────────────────────────────────

export class MindMapWebSocketProvider {
  readonly doc: Y.Doc;
  readonly awareness: Awareness;

  private readonly config: WebSocketProviderConfig;
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 1000;
  private shouldConnect = false;
  private _connected = false;
  private onStatusChange: (() => void) | null = null;

  // Event handler references for cleanup
  private readonly docUpdateHandler: (update: Uint8Array, origin: unknown) => void;
  private readonly awarenessChangeHandler: (
    changes: { added: number[]; updated: number[]; removed: number[] },
    origin: unknown,
  ) => void;

  constructor(doc: Y.Doc, config: WebSocketProviderConfig) {
    this.doc = doc;
    this.config = config;
    this.awareness = new Awareness(doc);

    // Set local awareness state
    this.awareness.setLocalStateField('user', {
      userId: config.user.userId,
      name: config.user.name,
      color: assignColor(config.user.userId),
      selectedNodeId: null,
      editingNodeId: null,
    } satisfies CollaboratorAwareness);

    // Bind handlers
    this.docUpdateHandler = (update: Uint8Array, origin: unknown) => {
      if (origin === 'remote') return;
      this.send({ action: 'yjs-update', data: uint8ToBase64(update) });
    };

    this.awarenessChangeHandler = (
      _changes: { added: number[]; updated: number[]; removed: number[] },
      origin: unknown,
    ) => {
      if (origin === 'remote') return;
      const encoded = encodeAwarenessUpdate(this.awareness, [this.doc.clientID]);
      this.send({ action: 'awareness', data: uint8ToBase64(encoded) });
    };

    // Subscribe to local doc updates and awareness changes
    this.doc.on('update', this.docUpdateHandler);
    this.awareness.on('change', this.awarenessChangeHandler);
  }

  // ── Public API ─────────────────────────────────────────────

  get connected(): boolean {
    return this._connected;
  }

  /** Register a callback for connection status changes. */
  onConnectionChange(cb: () => void): void {
    this.onStatusChange = cb;
  }

  connect(): void {
    this.shouldConnect = true;
    this.openWebSocket();
  }

  disconnect(): void {
    this.shouldConnect = false;
    this.clearReconnect();
    this.closeWebSocket();
  }

  destroy(): void {
    this.disconnect();
    this.doc.off('update', this.docUpdateHandler);
    this.awareness.off('change', this.awarenessChangeHandler);
    this.awareness.destroy();
  }

  // ── WebSocket lifecycle ────────────────────────────────────

  private openWebSocket(): void {
    if (this.ws) return;

    const url = `${this.config.wsEndpoint}?token=${encodeURIComponent(this.config.authToken)}&mapId=${encodeURIComponent(this.config.mapId)}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.addEventListener('open', () => {
      console.log('[WS] Connected to', url);
      this._connected = true;
      this.reconnectDelay = 1000;
      this.onStatusChange?.();

      // Initiate Yjs sync: send our state vector
      const sv = Y.encodeStateVector(this.doc);
      this.send({ action: 'yjs-sync-step1', data: uint8ToBase64(sv) });

      // Send initial awareness
      const awarenessUpdate = encodeAwarenessUpdate(this.awareness, [this.doc.clientID]);
      this.send({ action: 'awareness', data: uint8ToBase64(awarenessUpdate) });
    });

    ws.addEventListener('message', (event: MessageEvent) => {
      this.handleMessage(event);
    });

    ws.addEventListener('close', (e) => {
      console.log('[WS] Disconnected, code:', e.code, 'reason:', e.reason);
      this.ws = null;
      this._connected = false;
      this.onStatusChange?.();
      this.scheduleReconnect();
    });

    ws.addEventListener('error', (e) => {
      console.error('[WS] Error:', e);
      ws.close();
    });
  }

  private closeWebSocket(): void {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
      this._connected = false;
      this.onStatusChange?.();
    }
  }

  // ── Message handling ───────────────────────────────────────

  private handleMessage(event: MessageEvent): void {
    let msg: WsMessage;
    try {
      msg = JSON.parse(String(event.data)) as WsMessage;
    } catch {
      return;
    }

    switch (msg.action) {
      case 'yjs-update': {
        if (!msg.data) break;
        const update = base64ToUint8(msg.data);
        Y.applyUpdate(this.doc, update, 'remote');
        break;
      }

      case 'yjs-sync-step1': {
        // Remote is asking us for our updates since their state vector
        if (!msg.data) break;
        const remoteStateVector = base64ToUint8(msg.data);
        const diff = Y.encodeStateAsUpdate(this.doc, remoteStateVector);
        this.send({ action: 'yjs-sync-step2', data: uint8ToBase64(diff) });
        break;
      }

      case 'yjs-sync-step2': {
        // Remote sent us updates we were missing
        if (!msg.data) break;
        const update = base64ToUint8(msg.data);
        Y.applyUpdate(this.doc, update, 'remote');
        break;
      }

      case 'awareness': {
        if (!msg.data) break;
        const awarenessUpdate = base64ToUint8(msg.data);
        applyAwarenessUpdate(this.awareness, awarenessUpdate, 'remote');
        break;
      }

      case 'comment-update': {
        try {
          // data arrives as an object (not a string) from the WebSocket broadcast
          const payload = (typeof msg.data === 'string' ? JSON.parse(msg.data) : msg.data) as {
            type: string;
            comment?: Comment;
            commentId?: string;
            updates?: Partial<Comment>;
          };
          const store = useCommentStore.getState();
          if ((payload.type === 'added' || payload.type === 'add') && payload.comment) {
            store.addLocalComment(payload.comment);
          } else if ((payload.type === 'updated' || payload.type === 'update') && payload.comment) {
            // Backend sends the full updated comment object
            store.updateLocalComment(payload.comment.commentId, payload.comment);
          } else if ((payload.type === 'deleted' || payload.type === 'delete') && (payload.commentId || payload.comment?.commentId)) {
            store.removeLocalComment(payload.commentId ?? payload.comment!.commentId);
          }
        } catch {
          // ignore malformed comment-update messages
        }
        break;
      }
    }
  }

  private send(msg: WsMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const payload = { ...msg, mapId: this.config.mapId };
      this.ws.send(JSON.stringify(payload));
    }
  }

  // ── Reconnection ───────────────────────────────────────────

  private scheduleReconnect(): void {
    if (!this.shouldConnect) return;
    this.clearReconnect();
    this.reconnectTimer = setTimeout(() => {
      this.openWebSocket();
    }, this.reconnectDelay);
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30_000);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}
